import { Head, usePage } from '@inertiajs/react'

interface Props {
  title?: string
  description?: string
  canonicalUrl?: string
  canonicalPath?: string
  ogType?: string
  ogImage?: string
  noindex?: boolean
  schema?: object
}

interface SharedSeo {
  title?: string
  description?: string
  og_image?: string
  og_type?: string
  canonical?: string
  noindex?: boolean
}

export default function SeoHead({
  title,
  description,
  canonicalUrl,
  canonicalPath,
  ogType,
  ogImage,
  noindex,
  schema,
}: Props) {
  const shared = (usePage().props as { seo?: SharedSeo }).seo ?? {}

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://lendsolo.com'
  const resolvedTitle = title ?? shared.title ?? 'LendSolo'
  const resolvedDescription = description ?? shared.description ?? ''
  const resolvedOgType = ogType ?? shared.og_type ?? 'website'
  const resolvedOgImage = ogImage ?? shared.og_image ?? 'https://lendsolo.com/og/default.png'
  const resolvedNoindex = noindex ?? shared.noindex ?? false
  const resolvedUrl =
    canonicalUrl ?? (canonicalPath ? `${baseUrl}${canonicalPath}` : shared.canonical ?? baseUrl)

  const fullTitle = resolvedTitle.includes('LendSolo') ? resolvedTitle : `${resolvedTitle} | LendSolo`

  const jsonLdWithContext = schema ? { '@context': 'https://schema.org', ...schema } : null

  return (
    <Head>
      <title>{fullTitle}</title>
      <meta name="description" content={resolvedDescription} />
      {resolvedNoindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={resolvedUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:type" content={resolvedOgType} />
      <meta property="og:url" content={resolvedUrl} />
      <meta property="og:site_name" content="LendSolo" />
      <meta property="og:image" content={resolvedOgImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedOgImage} />
      {jsonLdWithContext && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(jsonLdWithContext).replace(/</g, '\\u003c'),
          }}
        />
      )}
    </Head>
  )
}
